import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {Tooltip} from "@mui/material";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import TablePagination from "@mui/material/TablePagination";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Checkbox } from "@mui/material";
import { convertTime } from "../../constants/common";
import DeleteIcon from "../../assets/images/delete_forever.svg";
import Success from "../../assets/images/check_circle.svg";
import Error from "../../assets/images/error.svg";
import DropdownMenu from "../DropdownMenu/DropdownMenu";
import EditableFileType from "../EditableFileType/EditableFileType";


const StyledTableFooter = styled(TableFooter)(({ theme }) => ({
	borderRadius: "0 0 5px 5px",
	background: "#F5F5F5",
}));

interface TableColumn {
	id: string;
	label: string;
	type?: string;
	align?: "left" | "right" | "center";
	render?: (row: any) => React.ReactNode;
}

interface DataTableProps {
	data: Record<string, any>[];
	columns: TableColumn[];
	totalCount: number;
	page: number;
	setPage?: React.Dispatch<React.SetStateAction<number>>;
	rowsPerPage: number;
	setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
	onPageChange?: (
		event: React.MouseEvent<HTMLButtonElement>,
		newPage: number
	) => void;
	onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onDelete?: (value: string,fileId: string,file_name: string) => void;
	showPagination?: boolean;
	selected?: number[];
	setSelected?: React.Dispatch<React.SetStateAction<number[]>>;
	onFileTypeUpdate?: (fileId: string, newType: string) => void;
}

interface TablePaginationActionsProps {
	count: number;
	page: number;
	rowsPerPage: number;
	totalCount: number;
	onPageChange: (
		event: React.MouseEvent<HTMLButtonElement>,
		newPage: number
	) => void;
}

const TablePaginationActions: React.FC<TablePaginationActionsProps> = (
	props
) => {
	const { count, totalCount, page, rowsPerPage, onPageChange } = props;

	const handleFirstPageButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		onPageChange(event, 0);
	};

	const handleBackButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		onPageChange(event, page - 1);
	};

	const handleNextButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		onPageChange(event, page + 1);
	};

	const handleLastPageButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
	};

	return (
		<Box sx={{ flexShrink: 0, ml: 2.5 }}>
			<IconButton
				onClick={handleFirstPageButtonClick}
				disabled={page === 0}
				aria-label="first page"
			>
				&lt;&lt;
			</IconButton>
			<IconButton
				onClick={handleBackButtonClick}
				disabled={page === 0}
				aria-label="previous page"
			>
				&lt;
			</IconButton>
			<IconButton
				onClick={handleNextButtonClick}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
				aria-label="next page"
			>
				&gt;
			</IconButton>
			<IconButton
				onClick={handleLastPageButtonClick}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
				aria-label="last page"
			>
				&gt;&gt;
			</IconButton>
		</Box>
	);
};

const DataTable: React.FC<DataTableProps> = ({
	data,
	columns,
	page,
	setPage,
	totalCount,
	rowsPerPage,
	setRowsPerPage,
	onPageChange,
	onRowsPerPageChange,
	onDelete,
	showPagination,
	selected,
	setSelected,
	onFileTypeUpdate,
}) => {
	const theme = useTheme();
	const [rows, setRows] = useState(data);

	useEffect(() => {
		setRows(Array.isArray(data) ? data : []);
	}, [data]);

	const handleChangePage = (
		event: React.MouseEvent<HTMLButtonElement> | null,
		newPage: number
	) => {
		setPage && setPage(newPage);
		if (onPageChange)
			onPageChange(event as React.MouseEvent<HTMLButtonElement>, newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage && setPage(0);
		if (onRowsPerPageChange) onRowsPerPageChange(event);
	};

	const renderStatusIcon = (status: string) => {
		switch (status) {
			case "Completed" :
				return (
					<Tooltip
						title="File processing complete"
						arrow
						placement="top">
							<img 
									src={Success}
									alt="Succes"
									/>
								</Tooltip>

					
				)
			case "Uploaded" :
				return (
					<img 
						src={Success}
						alt="Succes"
					/>
				)
			case "Failed" :
				return (
					<Tooltip
						title="Please delete the failed file to allow AI analysis of the contract workspace to complete"
						arrow
						placement="top">
						<img 
						src={Error}
						alt="Failed"
						/>
						</Tooltip>
					
				)
			default:
				return null;
		}
	}

	const menuItems:any = [
		{ label: "Download", id: "download" },
		{ label: "Delete", id: "delete" },
	];

	const handleOpen = (value: string,fileId:any,fileName:any ) => {
		onDelete?.(value,fileId,fileName)
	}
	
	return (
		<Box sx={{ width: "100%" }}>
			<TableContainer
				component={Paper}
				sx={{ maxHeight: 500, overflowY: "auto" }}
			>
				<Table aria-label="custom pagination table">
					<TableHead>
						<TableRow>
							{selected && setSelected && (
								<TableCell padding="checkbox">
									<Checkbox
										indeterminate={
											selected.length > 0 && 
											selected.length < rows.length
										}
										checked={
											rows.length > 0 && 
											selected.length === rows.length
										}
										onChange={(event) => {
											if (event.target.checked) {
												setSelected(
													rows.map((_, index) => index)
												);
											} else {
												setSelected([]);
											}
										}}
									/>
								</TableCell>
							)}
							{columns.map((column) => (
								<TableCell
									key={column.id}
									sx={{
										border: "none",
										background: "#F5F5F5",
										fontWeight: 700,
										p: 2,
									}}
								>
									<Typography
										sx={{
											fontSize: "0.875rem",
											color: theme.palette.primary.light,
										}}
										variant="body1"
									>
										{column.label}
									</Typography>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.length > 0 ? (
							rows.map((row, index) => {
								return (
									<TableRow
										hover
										tabIndex={-1}
										key={row.file_id}
									>
										{selected && setSelected && (
											<TableCell padding="checkbox">
												<Checkbox
													checked={selected.includes(index)}
													onChange={(event) => {
														if (event.target.checked) {
															setSelected(prev => prev ? [...prev, index] : [index]);
														} else {
															setSelected(prev => prev ? prev.filter((value) => value !== index) : []);
														}
													}}
												/>
											</TableCell>
										)}
										{columns.map((column) => (
											<TableCell
												key={column.id}
												align={column.align || "left"}
											>
												{
													<Typography
													sx={{
														fontSize: "1rem",
														color: theme.palette.text.primary,
													}}
													variant="body2"
												>
													{column.id === "status" ? ( 
										<Box display="flex" alignItems="center" gap={1} >
										{row[column.id]}
										{renderStatusIcon(row[column.id])}
										</Box>
										
									) : column.id === "file_type" && column.type === "editable_file_type" ? (
										// Render the editable file type component
										<EditableFileType 
											value={row[column.id] || ''} 
											fileId={row.file_id} 
											onUpdate={onFileTypeUpdate || (() => {})} 
										/>
									) : column.id === "created_at" ? (
										convertTime(row[column.id])
									) : column.id === "actions" ? (
										// If the column is "actions", render the action buttons
										column.render ? column.render(row) : (
											<DropdownMenu
												menuItems={menuItems}
												onSelect={(item:any) => handleOpen(item.id, row?.file_id,row?.file_name)}
												edit={false}
											/>
										)

									) : column.id === "select" ? (
										<Checkbox
											checked={selected?.includes(index)}
											onChange={(event) => {
												if (setSelected) {
													if (event.target.checked) {
														setSelected(prev => prev ? [...prev, index] : [index]);
													} else {
														setSelected(prev => prev ? prev.filter((value) => value !== index) : []);
													}
												}
											}}
										/>
									) : column.render ? (
										column.render(row)
									) : (
										row[column.id]
									)}
												</Typography>
												}
											</TableCell>
										))}
									</TableRow>
								);
							})
						) : (
							<TableRow>
								<TableCell
									colSpan={columns.length + (selected && setSelected ? 1 : 0)}
									align="center"
								>
									<Typography variant="body1">
										No data available
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
					{showPagination ?? <StyledTableFooter>
						<TableRow>
							<TablePagination
								rowsPerPageOptions={[
									5,
									10,
									25,
									{ label: "All", value: totalCount },
								]}
								colSpan={columns.length + (selected && setSelected ? 1 : 0)}
								count={totalCount}
								rowsPerPage={rowsPerPage}
								page={page}
								onPageChange={handleChangePage}
								onRowsPerPageChange={handleChangeRowsPerPage}
								ActionsComponent={TablePaginationActions as any}
							/>
						</TableRow>
					</StyledTableFooter>}
				</Table>
			</TableContainer>
		</Box>
	);
};

export default DataTable;
